import React, { useEffect, useMemo, useState } from 'react';
import { Table, Tag } from 'antd';
import dayjs from 'dayjs';
import tinycolor from 'tinycolor2';
import StatusService from '../../modules/Status/Service/StatusService';
import ReminderService from '../../services/ReminderService';
import AccountCell from '../Reminders/AccountCell';
import { getCreatorAccountCellProps } from '../Reminders/accountCellHelper';

interface StatusChangeRemindersProps {
  entityId?: number | string | null;
  entityName?: string;
  mode?: 'admin' | 'user';
  active?: boolean;
  refreshKey?: number | string;
}

const StatusChangeReminders: React.FC<StatusChangeRemindersProps> = ({
  entityId,
  entityName = 'proposal',
  mode = 'admin',
  active = true,
  refreshKey = 0,
}) => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [statuses, setStatuses] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);

  const statusMap = useMemo(() => {
    if (!statuses || statuses.length === 0) return {};
    return statuses.reduce<Record<string, any>>((acc, status) => {
      if (status?.id != null) {
        acc[String(status.id)] = status;
      }
      return acc;
    }, {});
  }, [statuses]);

  const fetchStatusReminders = async () => {
    if (!entityId) {
      setData([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    try {
      const params = {
        entityNames: entityName.toString().toUpperCase(),
        entityId: String(entityId),
        page: page - 1,
        size: pageSize,
      };
      const serviceCall =
        mode === 'admin'
          ? ReminderService.getAdminStatusChangeReminders
          : ReminderService.getMyStatusChangeReminders;

      const response: any = await serviceCall(params);
      const payload = response?.data ?? response ?? {};
      const list: any[] = Array.isArray(payload?.content)
        ? payload.content
        : Array.isArray(payload)
          ? payload
          : [];
      setData(list);
      const nextTotal =
        typeof payload?.totalElements === 'number'
          ? payload.totalElements
          : typeof payload?.total === 'number'
            ? payload.total
            : list.length;
      setTotal(nextTotal);
    } catch (error) {
      console.error('Statü değişikliği hatırlatmaları yüklenemedi:', error);
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!active) return;
    fetchStatusReminders();
  }, [active, entityId, entityName, mode, page, pageSize, refreshKey]);

  useEffect(() => {
    setPage(1);
  }, [entityId, entityName]);

  const handlePageChange = (nextPage: number, nextSize?: number) => {
    setPage(nextPage);
    if (nextSize && nextSize !== pageSize) {
      setPageSize(nextSize);
    }
  };

  useEffect(() => {
    const loadStatuses = async () => {
      try {
        const response: any = await StatusService.getAll();
        const payload = response?.data ?? response ?? [];
        if (Array.isArray(payload)) {
          setStatuses(payload);
        } else if (payload?.content && Array.isArray(payload.content)) {
          setStatuses(payload.content);
        }
      } catch (error) {
        console.error('Status listesi yüklenemedi:', error);
        setStatuses([]);
      }
    };
    loadStatuses();
  }, []);

  const columns = [
    {
      title: 'Statü',
      key: 'status',
      render: (_: any, record: any) => {
        const statusId = record?.status?.id ?? record?.statusId;
        const status =
          statusId != null ? statusMap[String(statusId)] : record?.status;
        const label = status?.name || record?.status?.name || `#${statusId ?? '—'}`;
        const color = status?.color || record?.status?.color || '#d1d5db';
        const borderColor = tinycolor(color).setAlpha(0.5).toRgbString();
        const backgroundColor = tinycolor(color).setAlpha(0.1).toRgbString();
        return (
          <Tag
            style={{
              color,
              borderColor,
              backgroundColor,
              borderRadius: '12px',
              padding: '5px 12px',
              fontWeight: 600,
              minWidth: 100,
              textAlign: 'center',
            }}
          >
            {label}
          </Tag>
        );
      },
    },
    {
      title: 'Kullanıcı',
      key: 'account',
      render: (_: any, record: any) => <AccountCell {...getCreatorAccountCellProps(record)} />,
    },
    {
      title: 'Tarih',
      dataIndex: 'reminderDate',
      key: 'reminderDate',
      render: (value?: string) => (value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-'),
    },
  ];

  return (
    <Table
      loading={loading}
      dataSource={data}
      columns={columns}
      pagination={{
        current: page,
        pageSize,
        total,
        showSizeChanger: true,
        onChange: handlePageChange,
      }}
      rowKey={(record) => record.id ?? `${record.entityId}-${record.reminderDate}`}
      locale={{ emptyText: 'Statü değişikliği bulunamadı.' }}
      scroll={{ x: 'max-content' }}
    />
  );
};

export default StatusChangeReminders;
